import React, { useEffect, useContext, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaPlus, FaMinus } from "react-icons/fa6";
import { useSelector, useDispatch } from 'react-redux';
import { archiveCohort, scheduleCohort, getCohorts } from '../../ducks/cohort'
import { AppLoadContext } from '../../components/ui/AppLoadContext'
import DataGrid from "../../components/DataGrid";
import { useNavigate } from "react-router-dom";
import withAuth from "../../components/hoc/withAuth";
import Loader from "../../components/ui/Loader";
import { getLatestRecord, toDateTime } from '../../utils/utils';
import ActionBar from "../../components/ui/ActionBar";
import ConfirmModal from "../../components/ui/ConfirmModal";
import messages from "../../utils/helper/messages";
import Icon from "../../components/ui/Icon";
import GridButton from "../../components/ui/GridButton";
import {
    ALERT_TYPE,
    COHORT_FILE_STATUS,
    COHORT_STATUS,
    COHORT_TRAINING_FLAG,
    COHORT_UPDATE_STATUS,
    MESSAGE_LISTEN_TYPE,
} from '../../constants';
import MergeCohortModal from "./MergeCohortModal";
import NewCohortModal from "./NewCohortModal";
import NewButton from "../../components/ui/NewButton";
import { AlertContext } from "../../context/AlertContext";
import { addMessageHandler, removeMessageHandler } from "../../services/sessionHandler";
import OverflowText from "../../components/ui/OverflowTooltip";
import UpdateCohortModal from './UpdateCohortModal';
import ViewCohortUpdateModal from './ViewCohortUpdateModal';

const CohortManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [cohortItem, setCohortItem] = useState(null);
    const [confirmModal, setConfirmModal] = useState(false);
    const [mergeModal, setMergeModal] = useState(false);
    const [addModal, setAddModal] = useState(false);
    const [viewUpdateModal, setViewUpdateModal] = useState(false);
    const [openCohortUpdateModal, setOpenCohortUpdateModal] = useState(false);
    const [cohortData, setCohortData] = useState(null);
    const [isCohortUpdateReUpload, setIsCohortUpdateReUpload] = useState(false);
    const [scheduling, setScheduling] = useState({});

    const isAppLoaded = useContext(AppLoadContext);
    const { showAlert } = useContext(AlertContext);
    const currentUser = useSelector((state) => state.user.currentUser);
    const cohort = useSelector((state) => state.cohort.cohorts);

    const cohortStatus = [COHORT_STATUS.ENRICHED, COHORT_STATUS.VALIDATED];
    const checkCohortStatus = [COHORT_STATUS.UPLOADED, COHORT_STATUS.ERROR];
    const checkCohortUpdateStatus = [COHORT_UPDATE_STATUS.Uploaded, COHORT_UPDATE_STATUS.Error];

    const handleConfirmClose = () => {
        setConfirmModal(false);
        setCohortItem(null);
    }

    const handleConfirm = () => {
        handleArchiveCohort(cohortItem);
        handleConfirmClose();
    }

    const handleMergeModalClose = () => {
        setMergeModal(false);
        dispatch(getCohorts(currentUser?.companyId, true));
    }

    const handleAddModalClose = () => {
        setAddModal(false);
        setCohortData(null);
    }

    const handleAddCohort = () => {
        handleAddModalClose();
        dispatch(getCohorts(currentUser?.companyId, true));
    }

    const handleArchiveConfirm = (id) => {
        setConfirmModal(true);
        setCohortItem(id);
    }

    const handleUpdateModalClose = () => {
        dispatch(getCohorts(currentUser?.companyId, true));
        setOpenCohortUpdateModal(false);
        setCohortData(null);
        setIsCohortUpdateReUpload(false);
    };

    const handleViewCohortUpdate = (data) => {
        setViewUpdateModal(true);
        setCohortData(data);
    }

    const handleViewCohortUpdateModalClose = () => {
        setViewUpdateModal(false);
        setCohortData(null);
    }

    useEffect(() => {
        if (isAppLoaded) {
            if (!currentUser) {
                return;
            }
            dispatch(getCohorts(currentUser?.companyId, true));
        }
    }, [isAppLoaded, currentUser]);

    useEffect(() => {
        const handler = () => {
            dispatch(getCohorts(currentUser?.companyId, true));
        };

        addMessageHandler(MESSAGE_LISTEN_TYPE.SCHEDULE_COHORT, handler);
        addMessageHandler(MESSAGE_LISTEN_TYPE.PROCESSED_COHORT_FILE, handler);

        return () => removeMessageHandler(handler);
    }, []);

    const handleCohortUpdate = (rowData) => {
        setCohortData(rowData);
        setOpenCohortUpdateModal(true);
    };

    const handleReuploadCohort = (rowData) => {
        setAddModal(true);
        setCohortData(rowData);
    };

    const handleCohortUpdateReupload = (rowData) => {
        setCohortData(rowData);
        setIsCohortUpdateReUpload(true);
        setOpenCohortUpdateModal(true);
    };

    const handleArchiveCohort = (id) => {
        dispatch(archiveCohort(id, () => {
            setCohortItem(null);
            showAlert(messages.success.deleteCohort, ALERT_TYPE.SUCCESS);
            dispatch(getCohorts(currentUser?.companyId, true));
        }, (error) => {
            const archiveError = error.response?.data?.error || error;
            showAlert(`${messages.error.prefix} ${archiveError}`, ALERT_TYPE.ERROR);
        }));
    }

    const handleScheduleCohort = (id) => {
        setScheduling((prev) => ({ ...prev, [id]: true }));

        dispatch(scheduleCohort(id, () => {
            setScheduling((prev) => ({ ...prev, [id]: false }));
            showAlert(messages.success.scheduleCohort, ALERT_TYPE.SUCCESS);
        })
        );
    };

    const isScheduleDisable = (item) => {
        return item.status !== COHORT_FILE_STATUS.VALIDATED;
    }

    const renderRowActions = (rowData) => {
        const { isLatest, isLatestUploaded, isLatestProcessed, status, id } = rowData;
        return (
            <>
                <GridButton variant="light" size="sm" className="me-1 py-0" title="View"
                    onClick={() => handleViewCohortUpdate(rowData)}>
                    <Icon icon="view" size={14} />
                </GridButton>
                <GridButton variant="light" size="sm" className="me-1 py-0" title="Reupload"
                    disabled={!checkCohortUpdateStatus.includes(status) || !(isLatestUploaded && isLatest)}
                    onClick={() => handleCohortUpdateReupload(rowData)}>
                    <Icon icon="re-upload" size={14} />
                </GridButton>
                <GridButton variant="light" size="sm" className="me-1 py-0" title="Results"
                    disabled={status !== COHORT_UPDATE_STATUS.Processed || !isLatestProcessed}
                    onClick={() => navigate(`/admin/cohort-management/results/${id}`)}
                >
                    <Icon icon="results" size={16} />
                </GridButton>
            </>
        )
    }


    const renderAggregateRowActions = (row) => {
        const subRows = row.original.subRows;
        const checkCohortStatuses = [COHORT_UPDATE_STATUS.Processed, COHORT_UPDATE_STATUS.Error];
        const latestRecord = subRows?.length > 0 ? getLatestRecord(subRows, 'modifiedDate') : null;
        const isArchiveEnabled = subRows?.length > 0 ? checkCohortStatuses.includes(latestRecord.status) : true;

        return (
            <>
                <GridButton variant="light" size="sm" className="me-1 my-1" title="Update" disabled={!cohortStatus.includes(row.original.status)}
                    onClick={() => handleCohortUpdate(row.original)}>
                    <Icon icon="update-cohort" size={14} />
                </GridButton>
                {checkCohortStatus.includes(row.original.status) ? (
                    <GridButton variant="light" size="sm" className="me-1 my-1" title="Reupload"
                        onClick={() => handleReuploadCohort(row.original)}>
                        <Icon icon="re-upload" size={14} />
                    </GridButton>
                ) : (
                    <GridButton variant="light" size="sm" className="me-1 my-1" title="Edit"
                        onClick={() => { navigate(`/admin/cohort-management/members/${row.original.id}/${row.original.status}`) }}>
                        <Icon icon="edit" size={14} />
                    </GridButton>
                )}
                <GridButton variant="light" size="sm" className="me-1 my-1" title="Merge"
                    disabled={row.original.status === COHORT_FILE_STATUS.ERROR}
                    onClick={() => { setMergeModal(true) }}>
                    <Icon icon="merge" size={14} />
                </GridButton>
                <GridButton variant="light" size="sm" className="me-1 my-1" title="Schedule"
                    disabled={isScheduleDisable(row.original)}
                    onClick={() => handleScheduleCohort(row.original.id)}>
                    {scheduling[row.original.id] ? (
                        <Loader />
                    ) : (
                        <Icon icon="schedule" size={16} />
                    )}
                </GridButton>
                <GridButton variant="light" size="sm" className="me-1 my-1" title="Archive" disabled={!isArchiveEnabled}
                    onClick={() => handleArchiveConfirm(row.original.id)}>
                    <Icon icon="archive" size={16} />
                </GridButton>
            </>
        )
    }

    const columns = [
        {
            id: 'expand',
            size: 32,
            minSize: 32,
            aggregatedCell: ({ row }) => {
                return (
                    <Button
                        size="sm"
                        variant="light"
                        onClick={row.getToggleExpandedHandler()}
                        style={{ cursor: 'pointer' }}>
                        {row.getIsExpanded() ? <FaMinus /> : <FaPlus />}
                    </Button>
                );
            },
        },
        {
            id: 'name',
            header: 'Cohort Name',
            accessorKey: 'name',
            maxSize: 400,
            sortingFn: 'text'
        },
        {
            id: 'status',
            header: 'Cohort status',
            accessorKey: 'status',
            cell: ({ row }) => (
                <div className="text-decoration-underline text-secondary cursor-pointer" onClick={() => {
                    navigate(`/admin/cohort-management/cohort-upload-overview/${row.original.id}`);
                }}>{row.original.status}</div>
            ),
            aggregatedCell: ({ row }) => (
                <div className="text-decoration-underline text-secondary cursor-pointer" onClick={() => {
                    navigate(`/admin/cohort-management/cohort-upload-overview/${row.original.id}`);
                }}>{row.original.status}</div>
            ),
            sortingFn: 'text'
        },
        {
            id: 'training',
            header: 'Training Cohort',
            accessorKey: 'training',
            size: 120,
            cell: ({ row }) => (
                <div>{COHORT_TRAINING_FLAG[row.original.training]}</div>
            ),
            sortingFn: 'text',
            aggregatedCell: ({ row }) => (
                <div>{COHORT_TRAINING_FLAG[row.original.training]}</div>
            ),
        },
        {
            id: 'fileName',
            header: 'Member List',
            accessorKey: 'fileName',
            sortingFn: 'text',
            cell: ({ row, cell }) => (<OverflowText id={cell.id}>{row.original.fileName}</OverflowText>),
            aggregatedCell: ({ row, cell }) => (<OverflowText id={cell.id}>{row.original.fileName}</OverflowText>),
        },
        {
            id: 'memberCount',
            header: 'Member Count',
            accessorKey: 'memberCount',
            size: 80,
        },
        {
            id: 'marketType',
            header: 'Market Type',
            accessorKey: 'marketType',
            size: 80,
        },
        {
            id: 'market',
            header: 'Market',
            accessorKey: 'market',
            size: 80,
        },
        {
            id: 'dateTime',
            header: 'Last Uploaded Date/Time',
            accessorKey: 'dateTime',
            sortType: 'date',
            accessorFn: (row) => (new Date(row.dateTime)),
            cell: ({ row }) => `${toDateTime(row.original.dateTime)}`,
            aggregatedCell: ({ row }) => `${toDateTime(row.original.dateTime)}`,
            sortingFn: 'datetime',
            size: 150,
        }, {
            name: 'actions',
            header: 'Actions',
            size: 201,
            cell: ({ row }) => (
                <div className="text-end">
                    {renderAggregateRowActions(row)}
                </div>
            ),
            aggregatedCell: ({ row }) => (
                <div className="text-end">
                    {renderAggregateRowActions(row)}
                </div>
            )
        },
    ];

    const subColumns = [
        {
            id: 'name',
            header: 'Update Name',
            accessorKey: 'name',
            size: 250,
            aggregatedCell: ({ row }) => row.original.name,
            cell: ({ row, getValue }) => {
                return (
                    <div style={{ paddingLeft: `${row.depth * 1}rem` }}>
                        {getValue()}
                    </div>
                );
            },
        },
        {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            cell: ({ row }) => (
                <div className="text-decoration-underline text-secondary cursor-pointer" onClick={() => {
                    navigate(`/admin/cohort-management/cohort-update-overview/${row.original.id}`);
                }}>{row.original.status}</div>
            ),
            aggregatedCell: ({ row }) => (
                <div className="text-decoration-underline text-secondary cursor-pointer" onClick={() => {
                    navigate(`/admin/cohort-management/cohort-update-overview/${row.original.id}`);
                }}>{row.original.status}</div>
            ),
            sortingFn: 'text',
        },
        {
            id: 'fileName',
            header: 'Member List',
            accessorKey: 'fileName',
            sortingFn: 'text',
            cell: ({ row, cell }) => (
                <OverflowText id={cell.id}>
                    {row.original.fileName}
                </OverflowText>
            ),
        },
        {
            id: 'memberUpdateInputCount',
            header: 'Member Count',
            accessorKey: 'memberUpdateInputCount',
            size: 80,
        },
        {
            id: 'dateTime',
            header: 'Updated DateTime',
            accessorKey: 'modifiedDate',
            sortType: 'date',
            accessorFn: (row) => new Date(row.modifiedDate),
            cell: ({ row }) => `${toDateTime(row.original.modifiedDate)}`,
            sortingFn: 'datetime',
            size: 150,
        },
        {
            name: 'actions',
            header: 'Actions',
            size: 193,
            cell: ({ row }) => <div className="text-end">{renderRowActions(row.original)}</div>,
        },
    ];

    const defaultSort = [
        {
            id: 'dateTime',
            desc: true,
        },
    ];

    const renderRowSubComponent = (row) => {
        const subRows = row.original.subRows;
        let updatedRows = [...subRows];

        if (updatedRows.length > 0) {
            const latestRecord = getLatestRecord(updatedRows, 'modifiedDate');
            const rowIndex = updatedRows.findIndex(row => row.id === latestRecord.id);
            updatedRows[rowIndex] = { ...latestRecord, isLatest: true };
        }

        const uploadedRows = updatedRows.filter(row => checkCohortUpdateStatus.includes(row.status));
        if (uploadedRows.length > 0) {
            const latestRecord = getLatestRecord(uploadedRows, 'modifiedDate');
            const rowIndex = updatedRows.findIndex(row => row.id === latestRecord.id);
            updatedRows[rowIndex] = { ...latestRecord, isLatestUploaded: true }
        }

        const processedRows = updatedRows.filter(row => row.status === COHORT_UPDATE_STATUS.Processed);
        if (processedRows.length > 0) {
            const latestRecord = getLatestRecord(processedRows, 'modifiedDate');
            const rowIndex = updatedRows.findIndex(row => row.id === latestRecord.id);
            updatedRows[rowIndex] = { ...latestRecord, isLatestProcessed: true }
        }

        return (
            <tr key={row.id + 'expanded'}>
                <td />
                <td colSpan={row.getVisibleCells().length - 1}>
                    <DataGrid
                        columns={subColumns}
                        data={updatedRows}
                        sort={defaultSort}
                        enableFilters={false}
                        isSubGrid={true}
                    />
                </td>
            </tr>
        );
    };

    return (
        <div className="h-100 d-flex flex-column">
            <ActionBar>
                <Button type="button" className="me-2" variant="outline-primary"
                    onClick={() => navigate(`/admin/cohort-management/archive`)}>
                    View Archive
                </Button>
                <NewButton onClick={() => setAddModal(true)} />
            </ActionBar>

            <div className="flex-grow-1">
                {cohort === null ? <Loader /> :
                    <DataGrid
                        columns={columns}
                        data={cohort}
                        sort={defaultSort}
                        enableFilters={false}
                        enableNesting={true}
                        rowSubComponent={renderRowSubComponent}
                    />}
            </div>

            {confirmModal && <ConfirmModal
                show={confirmModal}
                message={messages.confirm.deleteCohort}
                onConfirm={handleConfirm}
                onCancel={handleConfirmClose}
                onHideCallback={handleConfirmClose}
            />}

            {mergeModal && <MergeCohortModal
                show={mergeModal}
                onClose={handleMergeModalClose}
            />}

            {addModal && <NewCohortModal
                show={addModal}
                onCancel={handleAddModalClose}
                onUpload={handleAddCohort}
                data={cohortData}
            />}

            {openCohortUpdateModal && (
                <UpdateCohortModal
                    data={cohortData}
                    show={openCohortUpdateModal}
                    onCancel={handleUpdateModalClose}
                    isReUpload={isCohortUpdateReUpload}
                />
            )}

            {viewUpdateModal && (
                <ViewCohortUpdateModal
                    show={viewUpdateModal}
                    data={cohortData}
                    onCancel={handleViewCohortUpdateModalClose}
                />
            )}
        </div>
    );
}

export default withAuth(CohortManagement);
